/**
 * Opening baselines - reading a tag into its own read-only copy - and following
 * how far each has got (issue #97, phase D).
 *
 * OPENING A BIG PROGRAM IS SLOW (an import), so the server answers the open at
 * once with a status and the work goes on behind it. This asks again every
 * `POLL_MS` until the status is `ready` or `failed`, and stops the moment the
 * component using it unmounts - going to the baseline's screens is exactly what
 * unmounts the section that started the open, and a poll that outlived it would
 * keep asking for nothing.
 *
 * A failure of any kind - the server's `failed`, a refusal of the open itself, a
 * network error while polling - ends as a `failed` status with the words, never as
 * a throw: the caller draws one thing for "this did not open" and the baseline's
 * pipeline artifacts, which do not depend on it, stay available.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../../api'
import type { ApiError } from '../../api'
import type { BaselineStatus } from '../../schema'

/** How often a status is asked for while a baseline is opening. */
export const POLL_MS = 2000

/** A `not_opened` answer this many times running means the open never started. */
const NOT_STARTED_LIMIT = 3

const failedStatus = (tag: string, message: string): BaselineStatus => ({
  tag,
  ref: '',
  state: 'failed',
  message,
  started_at: null,
  progress: null,
  info: null,
})

export interface OpeningBaselines {
  /** The latest status of every baseline opened from here, by tag. */
  statuses: Record<string, BaselineStatus>
  /**
   * Open one and wait for it to end. Resolves with its final status, or null when
   * the component went away first.
   */
  open: (tag: string) => Promise<BaselineStatus | null>
}

export function useOpenBaseline(slug: string): OpeningBaselines {
  const alive = useRef(true)
  const [statuses, setStatuses] = useState<Record<string, BaselineStatus>>({})

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const open = useCallback(
    async (tag: string): Promise<BaselineStatus | null> => {
      const record = (status: BaselineStatus) => {
        if (alive.current) setStatuses((previous) => ({ ...previous, [tag]: status }))
      }
      let notStarted = 0
      try {
        let status = await api.openBaseline(slug, tag)
        for (;;) {
          if (!alive.current) return null
          record(status)
          if (status.state === 'ready' || status.state === 'failed') return status
          notStarted = status.state === 'not_opened' ? notStarted + 1 : 0
          if (notStarted >= NOT_STARTED_LIMIT) {
            const stalled = failedStatus(tag, 'Opening did not start. Try again.')
            record(stalled)
            return stalled
          }
          await new Promise((resolve) => setTimeout(resolve, POLL_MS))
          if (!alive.current) return null
          status = await api.baselineStatus(slug, tag)
        }
      } catch (refused) {
        const failed = failedStatus(tag, (refused as ApiError).message || 'Could not open it')
        record(failed)
        return alive.current ? failed : null
      }
    },
    [slug],
  )

  return { statuses, open }
}
